import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Bell, Clock, Moon, Zap, Trophy, Users, AlertCircle, Smartphone, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';
import { WebPushSettings } from '@/components/notifications/WebPushSettings';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { useLocalNotifications } from '@/hooks/useLocalNotifications';

interface NotificationPreferences {
  notifications_enabled: boolean;
  daily_checkin_reminder: boolean;
  credit_reset_notification: boolean;
  trial_ending_notification: boolean;
  new_content_notification: boolean;
  cycle_tracking_reminder: boolean;
  medication_reminder: boolean;
  challenge_ending_notifications: boolean;
  milestone_notifications: boolean;
  partnership_notifications: boolean;
  inactivity_notifications: boolean;
  frequency_preference: 'immediate' | 'daily_digest';
  quiet_hours_enabled: boolean;
  quiet_hours_start: string;
  quiet_hours_end: string;
  timezone: string;
  reminder_time: string;
}

export default function NotificationSettings() {
  const [hasPushToken, setHasPushToken] = useState(false);
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    notifications_enabled: true,
    daily_checkin_reminder: true,
    credit_reset_notification: true,
    trial_ending_notification: true,
    new_content_notification: true,
    cycle_tracking_reminder: false,
    medication_reminder: false,
    challenge_ending_notifications: true,
    milestone_notifications: true,
    partnership_notifications: true,
    inactivity_notifications: true,
    frequency_preference: 'immediate',
    quiet_hours_enabled: false,
    quiet_hours_start: '22:00',
    quiet_hours_end: '08:00',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    reminder_time: '09:00',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEnabling, setIsEnabling] = useState(false);

  // Native notification hooks
  const { 
    permissionStatus: pushPermissionStatus, 
    requestPermissionAndRegister 
  } = usePushNotifications();
  const { 
    permissionStatus: localPermissionStatus, 
    requestPermission: requestLocalPermission 
  } = useLocalNotifications();

  const isNative = Capacitor.isNativePlatform();
  const hasPermission = pushPermissionStatus === 'granted' || localPermissionStatus === 'granted';
  const permissionDenied = pushPermissionStatus === 'denied' && localPermissionStatus === 'denied';

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await (supabase as any)
        .from('user_notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setPreferences({
          notifications_enabled: data.notifications_enabled ?? true,
          daily_checkin_reminder: data.daily_checkin_reminder,
          credit_reset_notification: data.credit_reset_notification,
          trial_ending_notification: data.trial_ending_notification,
          new_content_notification: data.new_content_notification,
          cycle_tracking_reminder: data.cycle_tracking_reminder,
          medication_reminder: data.medication_reminder,
          challenge_ending_notifications: data.challenge_ending_notifications ?? true,
          milestone_notifications: data.milestone_notifications ?? true,
          partnership_notifications: data.partnership_notifications ?? true,
          inactivity_notifications: data.inactivity_notifications ?? true,
          frequency_preference: data.frequency_preference || 'immediate',
          quiet_hours_enabled: data.quiet_hours_enabled ?? false,
          quiet_hours_start: data.quiet_hours_start || '22:00',
          quiet_hours_end: data.quiet_hours_end || '08:00',
          timezone: data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          reminder_time: data.reminder_time || '09:00',
        });
      }

      // Check if user has push token registered
      if (Capacitor.isNativePlatform()) {
        const { data: tokenData } = await (supabase as any)
          .from('user_push_tokens')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();
        
        setHasPushToken(!!tokenData);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const savePreferences = async () => {
    setIsSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await (supabase as any)
        .from('user_notification_preferences')
        .upsert({
          user_id: user.id,
          ...preferences,
          updated_at: new Date().toISOString(),
        });

      if (error) throw error;

      toast.success('Notification preferences saved');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setIsSaving(false);
    }
  };

  const updatePreference = (key: keyof NotificationPreferences, value: boolean | string) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
  };

  return (
    <>
      <WebPushSettings />
      
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Push Notification Preferences
          </CardTitle>
          <CardDescription>
            Manage which notifications you receive on your mobile device
          </CardDescription>
        </CardHeader>
      <CardContent className="space-y-6">
        {/* Mobile Push Notifications Status & Enable Button */}
        {isNative && (
          <>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-primary/10 border border-primary/20 rounded-lg">
                <div className="flex items-center gap-3">
                  <Smartphone className="h-5 w-5 text-primary" />
                  <div className="space-y-0.5">
                    <Label className="text-base font-semibold">
                      Mobile Push Notifications
                    </Label>
                    <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                      {hasPermission ? (
                        <>
                          <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                          Enabled - You can receive push notifications
                        </>
                      ) : permissionDenied ? (
                        <>
                          <XCircle className="h-3.5 w-3.5 text-destructive" />
                          Denied - Enable in your device settings
                        </>
                      ) : (
                        <>
                          <AlertCircle className="h-3.5 w-3.5 text-muted-foreground" />
                          Not enabled yet
                        </>
                      )}
                    </p>
                  </div>
                </div>
                {!hasPermission && !permissionDenied && (
                  <Button
                    onClick={async () => {
                      setIsEnabling(true);
                      try {
                        await requestPermissionAndRegister();
                        await requestLocalPermission();
                        toast.success('Notifications enabled!');
                      } catch (error) {
                        console.error('Failed to enable notifications:', error);
                        toast.error('Failed to enable notifications');
                      } finally {
                        setIsEnabling(false);
                      }
                    }}
                    disabled={isEnabling}
                    size="sm"
                  >
                    {isEnabling ? 'Enabling...' : 'Enable'}
                  </Button>
                )}
              </div>
              
              {permissionDenied && (
                <p className="text-xs text-muted-foreground px-1">
                  To enable notifications, go to your device's Settings → Therappi → Notifications and turn them on.
                </p>
              )}
            </div>
            <Separator />
          </>
        )}

        {/* Master Toggle */}
        <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
          <div className="space-y-0.5">
            <Label htmlFor="master-notifications" className="text-base font-semibold">
              Enable All Notifications
            </Label>
            <p className="text-sm text-muted-foreground">
              Turn off to disable all app notifications
            </p>
          </div>
          <Switch
            id="master-notifications"
            checked={preferences.notifications_enabled}
            onCheckedChange={(checked) => updatePreference('notifications_enabled', checked)}
          />
        </div>

        <Separator />

        {/* Frequency Preference */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Zap className="h-4 w-4" />
            Notification Frequency
          </Label>
          <Select
            value={preferences.frequency_preference}
            onValueChange={(value) => updatePreference('frequency_preference', value as 'immediate' | 'daily_digest')}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="immediate">Immediate - Get notified right away</SelectItem>
              <SelectItem value="daily_digest">Daily Digest - One summary per day</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Separator />

        {/* Quiet Hours */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="quiet-hours" className="flex items-center gap-2">
                <Moon className="h-4 w-4" />
                Quiet Hours
              </Label>
              <p className="text-sm text-muted-foreground">
                Don't disturb during sleep time
              </p>
            </div>
            <Switch
              id="quiet-hours"
              checked={preferences.quiet_hours_enabled}
              onCheckedChange={(checked) => updatePreference('quiet_hours_enabled', checked)}
            />
          </div>

          {preferences.quiet_hours_enabled && (
            <div className="grid grid-cols-2 gap-4 pl-6">
              <div className="space-y-2">
                <Label htmlFor="quiet-start" className="text-xs">Start Time</Label>
                <Input
                  id="quiet-start"
                  type="time"
                  value={preferences.quiet_hours_start}
                  onChange={(e) => updatePreference('quiet_hours_start', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="quiet-end" className="text-xs">End Time</Label>
                <Input
                  id="quiet-end"
                  type="time"
                  value={preferences.quiet_hours_end}
                  onChange={(e) => updatePreference('quiet_hours_end', e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        <Separator />

        {/* Social & Accountability Notifications */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold flex items-center gap-2">
            <Users className="h-4 w-4" />
            Social & Accountability
          </h4>
          
          <div className="space-y-3 pl-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="challenge-ending" className="text-sm">Challenge Ending Soon</Label>
                <p className="text-xs text-muted-foreground">1 day before challenges end</p>
              </div>
              <Switch
                id="challenge-ending"
                checked={preferences.challenge_ending_notifications}
                onCheckedChange={(checked) => updatePreference('challenge_ending_notifications', checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="milestones" className="text-sm flex items-center gap-1">
                  <Trophy className="h-3 w-3" />
                  Streak Milestones
                </Label>
                <p className="text-xs text-muted-foreground">7, 14, 30, 60, 90 day achievements</p>
              </div>
              <Switch
                id="milestones"
                checked={preferences.milestone_notifications}
                onCheckedChange={(checked) => updatePreference('milestone_notifications', checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="partnerships" className="text-sm">Partnership Requests</Label>
                <p className="text-xs text-muted-foreground">New accountability partner requests</p>
              </div>
              <Switch
                id="partnerships"
                checked={preferences.partnership_notifications}
                onCheckedChange={(checked) => updatePreference('partnership_notifications', checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="inactivity" className="text-sm flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Inactivity Reminders
                </Label>
                <p className="text-xs text-muted-foreground">Keep your streak going notifications</p>
              </div>
              <Switch
                id="inactivity"
                checked={preferences.inactivity_notifications}
                onCheckedChange={(checked) => updatePreference('inactivity_notifications', checked)}
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* App Notifications */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold">App Reminders</h4>
          
          <div className="space-y-3 pl-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="daily-checkin" className="text-sm">Daily Check-in Reminder</Label>
                <p className="text-xs text-muted-foreground">Morning wellness check-in</p>
              </div>
              <Switch
                id="daily-checkin"
                checked={preferences.daily_checkin_reminder}
                onCheckedChange={(checked) => updatePreference('daily_checkin_reminder', checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="credit-reset" className="text-sm">Credit Reset</Label>
                <p className="text-xs text-muted-foreground">Monthly AI credits refreshed</p>
              </div>
              <Switch
                id="credit-reset"
                checked={preferences.credit_reset_notification}
                onCheckedChange={(checked) => updatePreference('credit_reset_notification', checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="trial-ending" className="text-sm">Trial Ending</Label>
                <p className="text-xs text-muted-foreground">3-day warning before expiry</p>
              </div>
              <Switch
                id="trial-ending"
                checked={preferences.trial_ending_notification}
                onCheckedChange={(checked) => updatePreference('trial_ending_notification', checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="new-content" className="text-sm">New Content</Label>
                <p className="text-xs text-muted-foreground">Courses, meditations, features</p>
              </div>
              <Switch
                id="new-content"
                checked={preferences.new_content_notification}
                onCheckedChange={(checked) => updatePreference('new_content_notification', checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="cycle-tracking" className="text-sm">Cycle Tracking</Label>
                <p className="text-xs text-muted-foreground">Period predictions & updates</p>
              </div>
              <Switch
                id="cycle-tracking"
                checked={preferences.cycle_tracking_reminder}
                onCheckedChange={(checked) => updatePreference('cycle_tracking_reminder', checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="medication" className="text-sm">Medication</Label>
                <p className="text-xs text-muted-foreground">Supplement reminders</p>
              </div>
              <Switch
                id="medication"
                checked={preferences.medication_reminder}
                onCheckedChange={(checked) => updatePreference('medication_reminder', checked)}
              />
            </div>
          </div>
        </div>

        <Separator />

        <div className="space-y-2">
          <Label htmlFor="reminder-time" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Default Reminder Time
          </Label>
          <Input
            id="reminder-time"
            type="time"
            value={preferences.reminder_time}
            onChange={(e) => updatePreference('reminder_time', e.target.value)}
            className="max-w-xs"
          />
          <p className="text-xs text-muted-foreground">
            Time for daily check-in and scheduled reminders
          </p>
        </div>

        <Button 
          onClick={savePreferences} 
          disabled={isSaving || isLoading}
          className="w-full"
        >
          {isSaving ? 'Saving...' : 'Save Preferences'}
        </Button>

        <p className="text-xs text-muted-foreground text-center">
          📱 Push notifications only work in the mobile app
        </p>
      </CardContent>
    </Card>
    </>
  );
}
